export type KnowledgeType = "file" | "note" | "faq" | "website" | "instructions" | "offer";
export type KnowledgeStatus = "Ready" | "Processing" | "Needs attention" | "Paused" | "Draft" | "Link only";

export interface KnowledgeItem {
  id: string;
  name: string;
  type: KnowledgeType;
  status: KnowledgeStatus;
  updatedAt?: string;
  content: string;
  detail?: string;
  url?: string;
  tone?: string;
  responseLength?: string;
  language?: string;
  avoid?: string;
  handover?: string;
  questions?: { id: string; question: string; answer: string; active: boolean }[];
  validFrom?: string;
  validUntil?: string;
  enabled?: boolean;
  canProcess?: boolean;
  hasOriginalFile?: boolean;
  isMarkdownFile?: boolean;
  associatedContact?: { customerId: string; contactId?: string | null; name: string };
  associatedWorkflow?: { workflowId: string; name: string };
  /** The owner's category in General knowledge; "" means deliberately none. */
  category?: string;
}

export type KnowledgeScope = "organisation" | "customer" | "workflow";

export const scopeLabels: Record<KnowledgeScope, string> = {
  organisation: "Organisation", customer: "Customer", workflow: "Workflow",
};

// Broadest first: this is also the order org-wide data is listed in.
export const scopeOrder: KnowledgeScope[] = ["organisation", "customer", "workflow"];

export function knowledgeScope(item: KnowledgeItem): KnowledgeScope {
  if (item.associatedWorkflow) return "workflow";
  if (item.associatedContact) return "customer";
  return "organisation";
}

export const typeLabels: Record<KnowledgeType, string> = {
  file: "File", note: "Note", faq: "Instruction", website: "Website", instructions: "AI instructions", offer: "Instruction",
};

export function isBusinessInstruction(item: KnowledgeItem): boolean {
  return item.type === "note" && item.name.trim().toLocaleLowerCase() === "about our business";
}

// The core org-wide items every business is expected to have; pinned above other knowledge.
export function isBusinessProfile(item: KnowledgeItem): boolean {
  if (item.associatedContact || item.associatedWorkflow) return false;
  return isBusinessInstruction(item) || (item.type === "website" && item.name.trim().toLocaleLowerCase() === "business website");
}

export function createKnowledgeItem(type: KnowledgeType): KnowledgeItem {
  return { id: crypto.randomUUID(), name: "", type, status: "Draft", content: "" };
}

const instructionTones: Record<string, { label: string; description: string }> = {
  friendly: { label: "Friendly & warm", description: "Welcoming and approachable" },
  professional: { label: "Professional", description: "Clear and courteous" },
  playful: { label: "Playful & casual", description: "Relaxed and conversational" },
  concise: { label: "Concise & direct", description: "Short and to the point" },
};

const responseLengthLabels: Record<string, string> = {
  default: "Let AI decide",
  brief: "Brief",
  balanced: "Balanced",
  detailed: "Detailed",
};

export function instructionsAsMarkdown(item: KnowledgeItem): string {
  if (item.type !== "instructions") return item.content;
  const hasStructuredInstructions = !!(item.tone || item.responseLength || item.language || item.avoid || item.handover);
  if (!hasStructuredInstructions) return item.content;

  const sections: string[] = [];
  const tone = item.tone ? instructionTones[item.tone] : null;
  if (tone) sections.push(`## Tone of voice\n\n**${tone.label}**\n\n${tone.description}`);
  sections.push(`## Response length\n\n${responseLengthLabels[item.responseLength ?? "default"] ?? item.responseLength}`);
  sections.push(`## Language preference\n\n${item.language?.trim() || "Match the customer"}`);
  if (item.content.trim()) sections.push(`## Additional instructions\n\n${item.content.trim()}`);
  if (item.avoid?.trim()) sections.push(`## Things to avoid\n\n${item.avoid.trim()}`);
  if (item.handover?.trim()) sections.push(`## When to hand over to staff\n\n${item.handover.trim()}`);
  return sections.join("\n\n");
}

export function knowledgeItemAsMarkdown(item: KnowledgeItem): string {
  if (item.type === "instructions") return instructionsAsMarkdown(item);
  if (item.type === "faq") {
    if (item.content.trim()) return item.content;
    return (item.questions ?? []).filter(question => question.active).map(question =>
      `## ${question.question.trim()}\n\n${question.answer.trim()}`
    ).join("\n\n");
  }
  if (item.type === "offer") {
    if (/^##\s+/m.test(item.content)) return item.content;
    if (!item.validFrom && !item.validUntil) return item.content;
    const sections = item.content.trim() ? [`## Offer details\n\n${item.content.trim()}`] : [];
    if (item.validFrom) sections.push(`## Starts on\n\n${item.validFrom}`);
    if (item.validUntil) sections.push(`## Ends on\n\n${item.validUntil}`);
    return sections.join("\n\n");
  }
  return item.content;
}

export interface MarkdownField {
  title: string;
  content: string;
}

export function markdownToFields(markdown: string): MarkdownField[] {
  const fields: MarkdownField[] = [];
  const preamble: string[] = [];
  let current: { title: string; lines: string[] } | null = null;

  for (const line of markdown.replace(/\r\n/g, "\n").split("\n")) {
    const heading = line.match(/^##\s+(.+?)\s*$/);
    if (heading) {
      if (current) fields.push({ title: current.title, content: current.lines.join("\n").trim() });
      current = { title: heading[1], lines: [] };
    } else if (current) {
      current.lines.push(line);
    } else {
      preamble.push(line);
    }
  }
  if (current) fields.push({ title: current.title, content: current.lines.join("\n").trim() });

  const leadingContent = preamble.join("\n").trim();
  if (leadingContent) fields.unshift({ title: "Content", content: leadingContent });
  return fields.length ? fields : [{ title: "Content", content: "" }];
}

export function fieldsAsMarkdown(fields: MarkdownField[]): string {
  return fields.map(field => `## ${field.title.trim() || "Untitled"}\n\n${field.content.trim()}`).join("\n\n");
}

export function validateKnowledgeItem(item: KnowledgeItem): string | null {
  if (!item.name.trim()) return "Give this file a name.";
  if (item.type === "website") {
    try {
      const url = new URL(item.url ?? "");
      if (!["https:", "http:"].includes(url.protocol)) return "Enter an http or https website URL.";
    } catch { return "Enter a valid website URL."; }
  }
  if (item.type === "faq") {
    if (item.content.trim()) {
      if (markdownToFields(item.content).some(field => !field.title.trim() || !field.content.trim())) return "Add a name and value to every field.";
    } else if (!item.questions?.length || item.questions.some(q => !q.question.trim() || !q.answer.trim())) {
      return "Add a name and value to every field.";
    }
  }
  if (["note", "offer"].includes(item.type) && !item.content.trim()) return "Add some content before saving.";
  if (item.validFrom && item.validUntil && item.validUntil < item.validFrom) return "The end date must be after the start date.";
  return null;
}

export function filterKnowledgeItems(items: KnowledgeItem[], query: string, type: string, status: string) {
  const term = query.trim().toLocaleLowerCase();
  return items.filter(item => {
    const matchesType = type === "all"
      || (type === "instructions"
        ? ["faq", "instructions", "offer"].includes(item.type) || isBusinessInstruction(item)
        : item.type === type && !(type === "note" && isBusinessInstruction(item)));

    return matchesType && (status === "all" || item.status === status) &&
      (!term || [item.name, item.content, item.url, item.associatedContact?.name, item.associatedWorkflow?.name, ...(item.questions ?? []).flatMap(q => [q.question, q.answer])]
        .filter(Boolean).join(" ").toLocaleLowerCase().includes(term));
  });
}

export function extractionStatus(status: string): KnowledgeStatus {
  if (status === "done") return "Ready";
  if (["pending", "processing"].includes(status)) return "Processing";
  return "Needs attention";
}
